import { INLINE_LINK, LinkPanel } from '@/components/bedrock';
import { PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { DataFlowDiagram } from '@/features/trust/components/DataFlowDiagram';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { TrustHubFooterSections } from './TrustHubFooterSections';
import { TrustHubGlance } from './TrustHubGlance';

import type { ReactElement } from 'react';

/**
 * Trust Center hub — `/trust`. Phase 6 Pass 3 iter 242 + iter 243.
 *
 * Per `pass-3-trust-center.md` § Goal: "Every B2B procurement
 * question answered without an email" + § Audit gates: "Marketing
 * — copy reviewed against brand voice; specific numbers preferred
 * over adjectives" + § Acceptance criteria: "every procurement
 * question from /product/research-archive/compliance-research.md
 * has a page-level answer."
 *
 * **Iter-242 scope**: the hub answers all 7 trust topics inline
 * with specific numbers from `/product/trust/compliance.md`. Each
 * topic gets a sub-page in subsequent iters (iter 243 → 250); when
 * a sub-page lands, that section flips to a card-with-deep-link.
 * Until then, the inline copy is the answer — no "coming soon"
 * placeholders, per `.claude/rules/quality-bar.md` "no half-done
 * work".
 *
 * **Design (Designer hat, locked)**: single rich page, scannable
 * by ctrl-F. Stat band up top for at-a-glance facts. Sections
 * use semantic `<section>` + `<h2>` for AT navigability. No
 * decorative graphics — procurement readers want text content
 * they can copy into vendor questionnaires.
 *
 * **Marketing voice (Marketer hat, locked)**: specific numbers
 * over adjectives. EU AI Act Article 50 enforcement date (2 Aug
 * 2026), GDPR Art. 12 SLAs (24-hour ack, 30-day fulfilment),
 * encryption at rest + TLS 1.3, EU data residency, SOC 2 Type I
 * Q3 2026 / Type II Q4 2026.
 *
 * **i18n**: every visitor-facing string resolves through `useT()`
 * under the `trusthub.*` namespace. The hub is not a binding legal
 * document, so it carries no `LegalTranslationNotice`.
 *
 * **Three-layer contract**: pure presentational component, no
 * data fetching. Compliance status changes (per Pass-3 spec)
 * land in iter-244+ via `compliance-status.json` config.
 */
/**
 * The ten trust documents `/trust` fronts, in the order a procurement reviewer
 * meets them: what the system is, then what it is measured against, then the
 * paperwork, then the rights.
 *
 * A table rather than ten hand-written blocks. Each entry's copy lives at
 * `trusthub.<id>.{heading,body,link}` and `topic-<id>` is the anchor that has
 * to keep working.
 */
const TRUST_DOCUMENTS = [
  { id: 'security', to: '/trust/security' },
  { id: 'status', to: '/status' },
  { id: 'questionnaire', to: '/trust/security-questionnaire' },
  { id: 'accessibility', to: '/trust/accessibility' },
  { id: 'subprocessors', to: '/trust/subprocessors' },
  { id: 'dpa', to: '/trust/dpa' },
  { id: 'privacy', to: '/trust/privacy' },
  { id: 'ai', to: '/trust/ai' },
  { id: 'compliance', to: '/trust/compliance' },
  { id: 'disclosure', to: '/trust/disclosure' },
] as const;

export function TrustHub(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('trusthub.meta.title'),
    description: t('trusthub.meta.description'),
    path: '/trust',
  });
  return (
    // The hub uses `PageHero` directly rather than the `LegalPage` shell the
    // documents under it share: that shell's eyebrow is a breadcrumb link back
    // to `/trust`, and this page IS `/trust`. A page linking to itself is the
    // kind of small wrongness a shared component quietly introduces.
    <main id="main" tabIndex={-1} className="bg-[color:var(--bd-page)]">
      <PageHero
        align="start"
        width="prose"
        eyebrow={t('trusthub.eyebrow')}
        title={t('trusthub.title')}
        description={
          <>
            {t('trusthub.intro.before')}{' '}
            <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
              admin@syncanix.com
            </a>{' '}
            {t('trusthub.intro.after')}
          </>
        }
      />

      <div className="mx-auto max-w-3xl px-6 pb-20 pt-10 text-[1.02rem] leading-[1.7] text-[color:var(--bd-body)]">
        <TrustHubGlance />

        <section aria-labelledby="data-flow" className="mb-12">
          <h2
            id="data-flow"
            className="mb-4 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
          >
            {t('trusthub.dataflow.heading')}
          </h2>
          <DataFlowDiagram />
        </section>

        {/* The ten documents as CARDS, not as ten prose sections.

            They were byte-identical in shape — heading, one paragraph, one link
            — repeated ten times down 3,500px with the same six-class heading
            string hand-written each time. A procurement reader arrives asking
            one question ("do they have a DPA?"), and answering it meant
            scrolling a wall to find a heading. Ten parallel items with one link
            each is a grid, and `LinkPanel` is the primitive for it: the whole
            card is the target, per `ui-design.md` § 17's simple shape — nothing
            else inside is interactive, so the real control wraps the content
            and there is no delegation to guard.

            Every `topic-*` id survives. Nothing in this repo links to them, but
            a security questionnaire answered months ago may well cite
            `/trust#topic-dpa`, and an anchor is a promise to whoever wrote it
            down. The `scroll-mt` rides the heading, which is what the anchor
            actually targets. */}
        <div className="mb-12 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {TRUST_DOCUMENTS.map((doc) => (
            <section key={doc.id} aria-labelledby={`topic-${doc.id}`}>
              <LinkPanel href={doc.to} className="flex h-full flex-col gap-2 p-5">
                <h2
                  id={`topic-${doc.id}`}
                  className="scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.12rem] font-semibold leading-[1.2] tracking-[-0.02em] text-[color:var(--bd-ink)]"
                >
                  {t(`trusthub.${doc.id}.heading`)}
                </h2>
                <p className="text-[0.94rem] leading-[1.62] text-[color:var(--bd-muted)]">
                  {t(`trusthub.${doc.id}.body`)}
                </p>
                {/* The card is the link, so this is the AFFORDANCE rather than a
                    second control — a nested link would be invalid HTML and
                    would strand a keyboard user between two targets.

                    The arrow comes from the COPY, not from `<CtaArrow>`: 45
                    catalog values across this site carry a directional arrow at
                    the edge of a link label, and `link-affordance-arrows.test.ts`
                    is what keeps each one mirrored in Arabic and Hebrew.
                    Rendering a component arrow here instead produced a visible
                    double arrow on the seven of ten labels that already had one
                    — and moving all ten out of the copy would have dropped that
                    guard's key count below its own not-vacuous floor. The three
                    labels that lacked an arrow gained one, which the grid made
                    obvious and the old one-per-line layout had hidden. */}
                <span className="mt-auto pt-1 text-[0.9rem] font-medium text-[color:var(--bd-link)]">
                  {t(`trusthub.${doc.id}.link`)}
                </span>
              </LinkPanel>
            </section>
          ))}
        </div>

        <TrustHubFooterSections />
      </div>
    </main>
  );
}
